import { BrowserBroadcast } from '@adapters/broadcast/browserBroadcast';
import { NavigationFlowHandler } from '@adapters/navigation/navigationFlowHandler';
import { ProcessPage, ProcessPageNavigation } from '@adapters/navigation/page';
import { Router } from '@angular/router';
import { Bank } from '@core/model/domain/bank';
import { Logger } from '@core/model/domain/logger';
import { NotificationSwitcher } from '@core/model/framework/notifications/notificationSwitcher';
import { Option } from '@core/model/framework/Result/Option';
import { Result } from '@core/model/framework/Result/Result';
import {
  ConnectionFormItemDto,
  RegionalBankPayload,
} from '@core/service/digital-factory/bank/types/dto/connectionFormDto';
import { AuthenticateToBankWithCredentials } from '@core/usecase/authenticateToBankWithCredentials';
import { GetAccounts } from '@core/usecase/getAccounts';
import { GetWebauthUrl } from '@core/usecase/getWebauthUrl';
import { SaveWebauthBankConnection } from '@core/usecase/saveWebauthBankConnection';
import {
  AbandonEvent,
  AcceptedProcessEvent,
  AccountsFoundEvent,
  AccountsSelectedEvent,
  BankChoiceEvent,
  ConsentEvent,
  ContinueAfterErrorEvent,
  ContinueEvent,
  GoBackToBankChoiceEvent,
  InternalErrorEvent,
  ParentProcessEvent,
  ProcessEventFactory,
  ProcessEventType,
  RetryAccountAnalysisEvent,
  WebauthWithRegionalBank,
} from '@shared/models/processEvents';
import { buildAutonomousRelayFragment } from '@modules/webauth-redirect/webauthRelayFragment';
import { isProbablyPhone } from '@shared/models/crossDeviceCapability';
import { ProcessEventService } from '@shared/services/process-event.service';
import { StoreService } from '@shared/services/store.service';
import { TrackerService } from '@shared/services/tracker.service';
import { AbortCase } from '@core/usecase/abortCase';
import { FailCase } from '@core/usecase/failCase';
import { GetBankById } from '@core/usecase/getBankById';
import { ConnectionStateEnum } from '@core/model/domain/bankConnection';
import { Subscription } from 'rxjs';

export class ProcessVM {
  isWebauthRedirectionGoingToPartner = false;

  private webauthTab: Window | null = null;
  private closeWebauthPopupSubscription: Subscription | null = null;

  constructor(
    private readonly processEventService: ProcessEventService,
    private readonly storeService: StoreService,
    private readonly navigationFlowHandler: NavigationFlowHandler,
    private readonly broadcastService: BrowserBroadcast,
    private readonly abortCase: AbortCase,
    private readonly failCase: FailCase,
    private readonly logger: Logger,
    private readonly router: Router,
    private readonly authenticateToBankWithCredentials: AuthenticateToBankWithCredentials,
    private readonly getAccounts: GetAccounts,
    private readonly isTrackingEnabled: boolean,
    private readonly tracker: TrackerService,
    private readonly notificationSwitcher: NotificationSwitcher,
    private readonly saveWebauthBankConnection: SaveWebauthBankConnection,
    private readonly getBankById: GetBankById,
    private readonly getWebauthUrl: GetWebauthUrl,
    private readonly isWebauthQrCodeEnabled: boolean
  ) {}

  async initUserJourney() {
    this.processEventService.subscribe(this.handleEvent.bind(this));
    this.navigationFlowHandler.handleProcessPageRefresh();

    await this.initServices();

    const journeyType = this.storeService.getVariableOrThrow('JOURNEY_TYPE');
    this.isWebauthRedirectionGoingToPartner = this.storeService.getVariableOrThrow(
      'IS_WEBAUTH_REDIRECTION_GOING_TO_PARTNER'
    );

    if (journeyType.type === 'NEW_USER') {
      this.handleNewUserJourney();
      return;
    }

    if (journeyType.type === 'WEBAUTH_SUCCESS') {
      await this.handleUserSucceededWebauth();
      return;
    }

    if (journeyType.type === 'WEBAUTH_FAILED') {
      this.handleUserFailedWebauth();
      return;
    }
  }

  private async handleEvent(event: AcceptedProcessEvent | ParentProcessEvent) {
    switch (event.type) {
      case ProcessEventType.CONSENT:
        this.handleConsent(event);
        break;
      case ProcessEventType.BANK_CHOICE:
        await this.handleBankChoice(event);
        break;
      case ProcessEventType.GO_BACK_TO_BANK_CHOICE:
        this.handleBackToBankChoice(event);
        break;
      case ProcessEventType.ACCOUNTS_FOUND:
        this.handleAccountsFoundEvent(event);
        break;
      case ProcessEventType.CONTINUE_AFTER_ERROR:
        this.handleContinueAfterError(event);
        break;
      case ProcessEventType.ABANDON:
        await this.handleAbandon(event);
        break;
      case ProcessEventType.ACCOUNTS_SELECTED:
        this.handleAccountsSelected(event);
        break;
      case ProcessEventType.CONTINUE:
        this.handleContinueEvent(event);
        break;
      case ProcessEventType.INTERNAL_ERROR:
        await this.handleInternalErrorEvent(event);
        break;
      case ProcessEventType.ASK_TO_RETRY_WEBAUTH:
        this.handleAskToRetryWebauth();
        break;
      case ProcessEventType.WEBAUTH_RESUMED_BY_PARTNER:
        this.handleWebauthResumedByPartner();
        break;
      case ProcessEventType.WEBAUTH_WITH_REGIONAL_BANK:
        this.handleWebauthWithRegionalBank(event);
        break;
      case ProcessEventType.RETRY_ACCOUNT_ANALYSIS:
        this.handleRetryAccountAnalysis(event);
        break;
      case ProcessEventType.CREDENTIALS_AUTHENTICATION:
        this.handleCredentialsAuthentication(event.payload);
        break;
      case ProcessEventType.RETRY_BANK_AUTHENTICATION:
        this.handleRetryBankAuthentication();
        break;
      case ProcessEventType.CONTINUE_WEBAUTH_ON_THIS_DEVICE:
        this.handleContinueWebauthOnThisDevice();
        break;
      case ProcessEventType.WINDOW_RESIZE_EVENT:
        break;
      default:
        this.neverCase(event);
    }
  }

  public neverCase(_input: never): never {
    throw new Error('should never go here');
  }

  private handleConsent(_consentEvent: ConsentEvent) {
    this.navigateToProcessPage({ page: 'BANK_CHOICE', params: null });
  }

  private handleRetryAccountAnalysis(_event: RetryAccountAnalysisEvent) {
    this.navigateToProcessPage({ page: 'BANK_CHOICE', params: null });
  }

  private async handleBankChoice(bankChoiceEvent: BankChoiceEvent) {
    const selectedBank = bankChoiceEvent.data;
    this.storeService.setVariable('SELECTED_BANK', selectedBank);

    const hasRegionalBanks = selectedBank.form.length !== 0;
    if (hasRegionalBanks) {
      this.navigateToProcessPage({ page: 'SELECT_REGIONAL_BANK', params: null });
      return;
    }

    this.handleWebauthAuthentication({ regionalBankPayload: null, selectedBank });
  }

  private handleWebauthWithRegionalBank(event: WebauthWithRegionalBank) {
    const regionalBankPayload = { value: event.regionalBankPayload };
    const selectedBank = this.storeService.getVariableOrThrow('SELECTED_BANK');

    this.handleWebauthAuthentication({ regionalBankPayload, selectedBank });
  }

  private handleBackToBankChoice(_event: GoBackToBankChoiceEvent) {
    this.navigationFlowHandler.handleGoBackViaInAppButton();
  }

  private handleAccountsFoundEvent(event: AccountsFoundEvent) {
    this.storeService.setVariable('FOUND_ACCOUNTS', event.data);
    const nbAccounts = event.data.length;

    if (!nbAccounts) {
      this.navigateToProcessPage({ page: 'NONE_ACCOUNTS', params: null });
      return;
    }

    this.navigateToProcessPage({ page: 'SELECT_ACCOUNTS', params: null });
  }

  private handleAccountsSelected(event: AccountsSelectedEvent) {
    this.storeService.setVariable('SELECTED_ACCOUNTS', event.selectedAccounts);
    this.navigateToProcessPage({ page: 'ACCOUNTS_ANALYSIS', params: null });
  }

  private async handleAbandon(_event: AbandonEvent) {
    const abortInput = {
      userToken: this.storeService.getVariableOrThrow('OPEN_BANKING_USER_TOKEN'),
      userId: this.storeService.getVariableOrThrow('USER_ID'),
    };

    try {
      await this.abortCase.execute(abortInput);
      this.storeService.reset();
      this.logger.logEvent('END_OF_SEQUENCE', { success: false, hasErrors: false });
      window.parent.postMessage('ABORTED', '*');
    } catch {
      await this.failCase.execute(abortInput);
      this.storeService.reset();
      this.logger.logEvent('END_OF_SEQUENCE', { success: false, hasErrors: false });
      window.parent.postMessage('ERROR', '*');
    }
  }

  private handleContinueEvent(_event: ContinueEvent) {
    this.storeService.reset();
    this.logger.logEvent('END_OF_SEQUENCE', { success: true, hasErrors: false });
    window.parent.postMessage('SUCCESS', '*');
  }

  private handleContinueAfterError(_event: ContinueAfterErrorEvent) {
    this.storeService.reset();
    this.logger.logEvent('END_OF_SEQUENCE', { success: false, hasErrors: true });
    window.parent.postMessage('ERROR', '*');
  }

  private async handleInternalErrorEvent(event: InternalErrorEvent) {
    this.logger.logEvent('Internal error event', event.payload);

    await this.failCase.execute({
      userToken: this.storeService.getVariableOrThrow('OPEN_BANKING_USER_TOKEN'),
      userId: this.storeService.getVariableOrThrow('USER_ID'),
    });

    this.navigateToErrorRoute();
  }

  private handleAskToRetryWebauth() {
    const processPage = this.storeService.getVariableOrThrow('PROCESS_PAGE');
    const hasUserLeftWaitOnExternalBankLoginPage = processPage !== 'WAIT_ON_EXTERNAL_BANK_LOGIN';

    if (!hasUserLeftWaitOnExternalBankLoginPage) {
      this.navigateToProcessPage({
        page: 'BANK_CONNECTION_FAILURE_RETRY',
        params: null,
      });
    }
  }

  /*
    The partner reloaded the widget where the bank came back and goes on with
    the connection there; this page, still on the waiting screen, only says so
    — acting too would mean two widgets driving the same connection.
  */
  private handleWebauthResumedByPartner() {
    if (this.storeService.getVariableOrThrow('PROCESS_PAGE') === 'WAIT_ON_EXTERNAL_BANK_LOGIN') {
      this.navigateToProcessPage({ page: 'WEBAUTH_RESUMED_BY_PARTNER', params: null });
    }
  }

  private navigateToProcessPage<T extends ProcessPage>(processPageNavigation: ProcessPageNavigation<T>) {
    this.storeService.setVariable('PROCESS_PAGE', processPageNavigation.page);
    this.storeService.setVariable('PROCESS_PAGE_PARAMS', processPageNavigation.params);
    window.scrollTo({ top: 0 });

    this.navigationFlowHandler.handleProcessNavigation(processPageNavigation.page);
  }

  private navigateToErrorRoute() {
    this.router.navigate(['error'], { state: { isWrongUrl: false } });
  }

  private async handleCredentialsAuthentication({
    form,
    selectedBank,
  }: {
    form: ConnectionFormItemDto[];
    selectedBank: Bank;
  }) {
    this.navigateToProcessPage({ page: 'LOADING', params: null });
    const userId = this.storeService.getVariableOrThrow('USER_ID');
    const userToken = this.storeService.getVariableOrThrow('OPEN_BANKING_USER_TOKEN');

    try {
      const authenticateResult = await this.authenticateToBankWithCredentials.execute({
        bankId: selectedBank.uid,
        form,
        userId,
        userToken,
      });

      const bankConnection = Result.unwrap(authenticateResult);
      const accountsResult = await this.getAccounts.execute({
        connectionId: bankConnection.connectionId,
        userId,
        userToken,
      });
      const accounts = Result.unwrap(accountsResult);

      this.processEventService.push(ProcessEventFactory.SpawnAccountsFoundEvent(accounts));
      this.storeService.setVariable('SELECTED_CONNECTION_ID', bankConnection.connectionId);
    } catch (_e) {
      this.processEventService.push(
        ProcessEventFactory.SpawnInternalErrorEvent({
          error: 'Error during authentication with credentials authentication',
          errorOrigin: 'ProcessComponent',
        })
      );
    }
  }

  private handleWebauthKO() {
    this.broadcastService.getMessageFromLabel('CONNECTION_EXTERNAL_BANK_KO').subscribe(() => {
      const isWebauthCancelled =
        this.storeService.getVariableOrThrow('PROCESS_PAGE') === 'WAIT_ON_EXTERNAL_BANK_LOGIN' ? false : true;

      if (!isWebauthCancelled) {
        this.processEventService.push(ProcessEventFactory.SpawnAskToRetryWebauth());
      }
    });
  }

  private handleWebauthHasUserStoppedWaitingCheck() {
    this.broadcastService.getMessageFromLabel('WEBAUTH_POPUP_REACHED').subscribe(() => {
      const hasUserStoppedWaiting =
        this.storeService.getVariableOrThrow('PROCESS_PAGE') === 'WAIT_ON_EXTERNAL_BANK_LOGIN' ? false : true;

      this.broadcastService.publish({
        label: 'WEBAUTH_POPUP_ANSWER_HAS_USER_STOPPED_WAITING',
        data: { hasUserStoppedWaiting },
      });
    });
  }

  private handleRetryBankAuthentication() {
    this.navigateToProcessPage({ page: 'BANK_CHOICE', params: null });
  }

  /*
    Need to be initialized here in case of refresh
  */
  private async initServices() {
    await this.initializeTaggingTracker();
    await this.initializeNotificationSwitcher();
  }

  private handleWebauthAuthentication({
    regionalBankPayload,
    selectedBank,
  }: {
    regionalBankPayload: Option<RegionalBankPayload>;
    selectedBank: Bank;
  }) {
    this.storeService.setVariable('WEBAUTH_BANK_URL', null);
    this.storeService.setVariable('WEBAUTH_AWAITING_DEVICE_CHOICE', false);
    this.storeService.setVariable('WEBAUTH_REGIONAL_BANK_PAYLOAD', regionalBankPayload?.value ?? null);
    /*
      One journey, one key, chosen HERE: whoever asks the bank url afterwards
      (this page, or the relay through its fragment) hands it to the backend,
      and the waiting screen polls the journey status with it. Persisted: the
      waiting screen may be re-created after a reload.
    */
    this.storeService.setVariable('WEBAUTH_JOURNEY_KEY', this.newJourneyKey());

    if (this.shouldOfferQrCode()) {
      /*
        No tab yet: the user first picks between this computer and their phone.
        Opening it now would steal the focus and hide the choice behind the
        bank page. The tab is opened later, from the choice click handler,
        which is a user gesture too — so it is never blocked. The url fetched
        here only feeds the qr code: "this device" goes through the relay.
      */
      this.storeService.setVariable('WEBAUTH_AWAITING_DEVICE_CHOICE', true);
      this.navigateToWaitOnExternalBankLogin(regionalBankPayload);
      void this.fetchWebauthUrlForQrCode({ regionalBankPayload, selectedBank });
      return;
    }

    // No qr code offered: the relay is opened at once with an autonomous
    // fragment and decides its own completion mode.
    this.openRelayAndWait({ regionalBankPayload, selectedBank });
  }

  /*
    The qr code is offered on a computer only: a phone cannot scan its own
    screen, so it goes straight to the bank tab.
  */
  private shouldOfferQrCode(): boolean {
    return this.isWebauthQrCodeEnabled && !isProbablyPhone();
  }

  private handleContinueWebauthOnThisDevice() {
    const selectedBank = this.storeService.getVariableOrThrow('SELECTED_BANK');
    const storedRegionalBankPayload = this.storeService.getVariable('WEBAUTH_REGIONAL_BANK_PAYLOAD');

    this.storeService.setVariable('WEBAUTH_AWAITING_DEVICE_CHOICE', false);

    /*
      Through the relay, NOT straight to the url prefetched for the qr: that
      url was asked without a mode, so its journey is bound to land on the
      widget page — right only if this page survives the open. A "desktop" can
      be a tablet whose partner webview loads the tab in place and kills this
      page; only the relay sees that (no opener) and asks a partner restore
      instead. It asks the backend again under the same journey key: the
      backend re-aims the journey (same nonce, same bank session as the qr,
      new destination) rather than opening a second one.
    */
    this.openRelayAndWait({
      regionalBankPayload: storedRegionalBankPayload ? { value: storedRegionalBankPayload } : null,
      selectedBank,
    });
  }

  private openRelayAndWait({
    regionalBankPayload,
    selectedBank,
  }: {
    regionalBankPayload: Option<RegionalBankPayload>;
    selectedBank: Bank;
  }) {
    /*
      window.open must be called synchronously in the click handler, otherwise
      the browser blocks it. A tab and not a same-window navigation: the widget
      runs inside the partner iframe and bank portals refuse to be framed
      (X-Frame-Options); window.open escapes the iframe and keeps this page
      alive to resume.
    */
    this.webauthTab = this.openRelayTab({ selectedBank, regionalBankPayload });

    if (this.webauthTab === null) {
      this.logger.logTrace('WEBAUTH_REDIRECT: no window handle returned by window.open');

      /*
        window.open returned no handle — which does NOT prove no tab opened.
        Seen in the field on a desktop: the browser opens the relay noopener
        (extension, policy, webview quirk), hands back null, and the relay —
        seeing no opener — rightly asks the partner restore… while this page
        wrongly declared a failure. Same story on a phone (webview navigating
        in place). Ruling anything out is guesswork: this page's one job is to
        poll, so it stays on the waiting screen and lets the journey status
        decide. A genuinely blocked popup ends in the poll timeout and the
        retry screen; the waiting screen offers "choose another bank" anyway.
      */
      if (this.storeService.getVariable('PROCESS_PAGE') !== 'WAIT_ON_EXTERNAL_BANK_LOGIN') {
        this.navigateToWaitOnExternalBankLogin(regionalBankPayload);
      }

      return;
    }

    /* Already displayed when the user was offered the device choice. */
    if (this.storeService.getVariable('PROCESS_PAGE') !== 'WAIT_ON_EXTERNAL_BANK_LOGIN') {
      this.navigateToWaitOnExternalBankLogin(regionalBankPayload);
    }

    /*
      Subscribed in every mode: with the server callback active the /webauth
      outcome page broadcasts even on journeys initiated with a partner
      restore url; without it no page ever broadcasts, so this stays inert.
    */
    if (this.closeWebauthPopupSubscription === null) {
      this.closeWebauthPopupSubscription = this.broadcastService
        .getMessageFromLabel('CLOSE_WEBAUTH_POPUP')
        .subscribe(() => {
          this.webauthTab?.close();
        });
    }
  }

  /*
    One way to reach the bank, whatever the device: the self-sufficient relay.
    This page may die on the open (a webview — phone OR tablet — loads the tab
    in place), so the target must be a page that reads its fragment and
    completes on its own, choosing where the journey lands from what it sees
    at that moment. The bank url itself never travels in the fragment: the
    relay asks the backend for it with the token (an open route redirecting to
    any url found in its fragment would be an open redirect).
  */
  private openRelayTab({
    selectedBank,
    regionalBankPayload,
  }: {
    selectedBank: Bank;
    regionalBankPayload: Option<RegionalBankPayload>;
  }): Window | null {
    const journeyKey = this.storeService.getVariableOrThrow('WEBAUTH_JOURNEY_KEY');
    const fragment = buildAutonomousRelayFragment({
      token: this.storeService.getVariableOrThrow('OPEN_BANKING_USER_TOKEN'),
      bankId: selectedBank.uid,
      journeyKey,
      regionalForm: regionalBankPayload
        ? {
            value: {
              fieldName: regionalBankPayload.value.fieldName,
              websiteUrl: regionalBankPayload.value.websiteUrl,
            },
          }
        : null,
    });

    /*
      The attempt query makes every opening unique OUTSIDE the fragment: url
      routers and deduplicators (a partner app handing the url to the default
      browser, a browser reusing a tab on the same path) ignore or lose the
      fragment, and two attempts differing only by it can be declared "the
      same url" — the previous bank then reopens. A changed query defeats any
      equality check and makes a same-document navigation impossible. The
      journey key is opaque and already public to our server; the relay
      ignores unknown query parameters.
    */
    return window.open(
      `${window.location.origin}/webauth-redirect?attempt=${encodeURIComponent(journeyKey)}${fragment}`
    );
  }

  private navigateToWaitOnExternalBankLogin(regionalBankPayload: Option<RegionalBankPayload>) {
    this.navigateToProcessPage({
      page: 'WAIT_ON_EXTERNAL_BANK_LOGIN',
      params: { regionalBankName: regionalBankPayload?.value.name },
    });
  }

  /*
    Fetched while the choice is displayed, for the qr code only: picking "this
    device" opens the relay, which asks its own url with its own mode.
  */
  private async fetchWebauthUrlForQrCode({
    regionalBankPayload,
    selectedBank,
  }: {
    regionalBankPayload: Option<RegionalBankPayload>;
    selectedBank: Bank;
  }) {
    try {
      await this.fetchWebauthUrl({ regionalBankPayload, selectedBank });
    } catch (error) {
      this.logger.logException(error);

      if (this.storeService.getVariable('PROCESS_PAGE') === 'WAIT_ON_EXTERNAL_BANK_LOGIN') {
        this.navigateToProcessPage({ page: 'BANK_CONNECTION_FAILURE_RETRY', params: null });
      }
    }
  }

  private async fetchWebauthUrl({
    regionalBankPayload,
    selectedBank,
  }: {
    regionalBankPayload: Option<RegionalBankPayload>;
    selectedBank: Bank;
  }): Promise<{ url: string }> {
    let form: Option<ConnectionFormItemDto[]> = null;

    if (regionalBankPayload?.value) {
      const { fieldName, websiteUrl } = regionalBankPayload.value;
      form = { value: [{ name: fieldName, value: websiteUrl }] };
    }

    // The historic body plus the journey key; no mode: this page stays alive
    // and polls, the widget outcome page is what the backend assumes.
    const result = await this.getWebauthUrl.execute({
      tagging: this.storeService.getVariable('TAGGING_INIT_DATA_IN_BASE64') ?? undefined,
      userToken: this.storeService.getVariableOrThrow('OPEN_BANKING_USER_TOKEN'),
      bankId: selectedBank.uid,
      form,
      journeyKey: this.storeService.getVariableOrThrow('WEBAUTH_JOURNEY_KEY'),
    });

    const { url } = Result.unwrap(result);

    this.storeService.setVariable('WEBAUTH_BANK_URL', url);

    return { url };
  }

  /*
    An opaque client token, not a secret: the backend only indexes the journey
    with it. randomUUID is missing from older webviews (iOS < 15.4): fall back
    to random hex from getRandomValues, same alphabet the backend accepts.
  */
  private newJourneyKey(): string {
    if (typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  private handleNewUserJourney() {
    this.handleWebauthKO();
    this.handleWebauthHasUserStoppedWaitingCheck();

    if (this.storeService.getVariable('PROCESS_PAGE') == null) {
      this.navigateToProcessPage({ page: 'CONSENT', params: null });
      return;
    }

    this.resumeDeviceChoiceIfInterrupted();
  }

  /*
    The page was refreshed while the bank url was still in flight: nobody would
    ask for it again and the device choice would wait forever on its loader.
  */
  private resumeDeviceChoiceIfInterrupted() {
    const isAwaitingDeviceChoice = this.storeService.getVariable('WEBAUTH_AWAITING_DEVICE_CHOICE') === true;
    const isOnWaitingPage = this.storeService.getVariable('PROCESS_PAGE') === 'WAIT_ON_EXTERNAL_BANK_LOGIN';
    const hasBankUrl = this.storeService.getVariable('WEBAUTH_BANK_URL') != null;
    const selectedBank = this.storeService.getVariable('SELECTED_BANK');

    if (!isAwaitingDeviceChoice || !isOnWaitingPage || hasBankUrl || selectedBank == null) {
      return;
    }

    const storedRegionalBankPayload = this.storeService.getVariable('WEBAUTH_REGIONAL_BANK_PAYLOAD');

    void this.fetchWebauthUrlForQrCode({
      regionalBankPayload: storedRegionalBankPayload ? { value: storedRegionalBankPayload } : null,
      selectedBank,
    });
  }

  private async initializeTaggingTracker() {
    const taggingInitDataInBase64 = this.storeService.getVariable('TAGGING_INIT_DATA_IN_BASE64');

    if (this.isTrackingEnabled) {
      if (taggingInitDataInBase64 == null) {
        this.logger.logTrace('ERROR - No tagging provided - Tagging tracker not initialized');
      } else {
        await this.tracker.initializeTaggingTracker(taggingInitDataInBase64);
      }
    }
  }

  private async initializeNotificationSwitcher() {
    await this.notificationSwitcher.initialize();
  }

  private async handleUserSucceededWebauth() {
    try {
      this.displayLoading();
      const userId = this.storeService.getVariableOrThrow('USER_ID');
      const userToken = this.storeService.getVariableOrThrow('OPEN_BANKING_USER_TOKEN');
      const userJourney = this.storeService.getVariableOrThrow('JOURNEY_TYPE');

      if (userJourney.type != 'WEBAUTH_SUCCESS') {
        throw new Error('User must be in webauth success state ');
      }

      const bankConnectionResult = await this.saveWebauthBankConnection.execute({
        webauthPopupUserToken: userToken,
        webauthPopupConnectionId: userJourney.payload.externalConnectionId,
      });
      const { connectionId, bankId, state } = Result.unwrap(bankConnectionResult);

      if (state !== ConnectionStateEnum.VALID) {
        this.handleUserFailedWebauth();
        this.logger.logException(new Error(`webauth-connection state is not valid. State is ${state}`));
        return;
      }

      this.storeService.setVariable('SELECTED_CONNECTION_ID', connectionId);

      const bankResult = await this.getBankById.execute({ bankId });
      const bank = Result.unwrap(bankResult);
      this.storeService.setVariable('SELECTED_BANK', bank);

      const activeWaiter = new Promise((resolve) => {
        setTimeout(() => {
          resolve(undefined);
        }, 1000);
      });
      await activeWaiter;

      const result = await this.getAccounts.execute({ connectionId, userId, userToken });
      const accounts = Result.unwrap(result);
      this.processEventService.push(ProcessEventFactory.SpawnAccountsFoundEvent(accounts));
    } catch (e) {
      this.logger.logException(e);
      this.navigateToProcessPage({ page: 'BANK_CONNECTION_FAILURE_RETRY', params: null });
    }
  }

  private handleUserFailedWebauth() {
    this.displayLoading();
    this.navigateToProcessPage({ page: 'BANK_CONNECTION_FAILURE_RETRY', params: null });
  }

  private displayLoading() {
    this.storeService.setVariable('PROCESS_PAGE', 'LOADING');
  }
}
